import styled, { keyframes } from "styled-components";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LogIn,
  LogOut,
  BedDouble,
  Brush,
  Search,
  SlidersHorizontal,
  Loader2,
  Check,
  SearchX,
  Info,
  AlertTriangle,
  Timer,
  HandCoins,
} from "lucide-react";
import { router, Head, usePoll } from "@inertiajs/react";

import { apiFetch } from "../_kit/api";
import BookingSourceIcon from "../_kit/BookingSourceIcon";

import { theme } from "../_kit/theme";
import PageShell from "../_kit/PageShell";
import PropertyStatusBar from "../_kit/PropertyStatusBar";
import FoCommentsModal from "../_kit/FoCommentsModal";
import BookingDrawer from "../_kit/BookingDrawer";
import { FoNotesButton, GuestNotesButton } from "../_kit/NotesButtons";
import { formatDate, timeWithDayPart } from "../_kit/dateFormat";
import { cleanComment } from "../_kit/text";
import { readDrawerLineId, useDrawerUrlSync } from "../_kit/useDrawerUrl";
import { useT } from "../_kit/i18n";

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.4rem;
`;

const Subtitle = styled.p`
  margin: 0.25rem 0 0;
  color: ${theme.muted};
  font-size: 0.9rem;
`;

const Counters = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
`;

const CounterCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1rem 1.1rem;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 14px;
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 12px;
  color: ${theme.ink};
  background: ${theme.goldSoft};
  flex: 0 0 auto;
`;

const CounterValue = styled.div`
  font-size: 1.7rem;
  font-weight: 800;
  line-height: 1;
  color: ${theme.ink};
`;

const CounterLabel = styled.div`
  font-size: 0.85rem;
  color: ${theme.muted};
  margin-top: 0.2rem;
`;

const CounterSub = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${theme.goldDark};
  margin-top: 0.1rem;
`;

const Section = styled.section`
  margin-top: 1.5rem;
`;

// Sticks just under the top bar (its height is published as --fd-bar-height by PageShell) so the
// section a row belongs to — "Check-in today", search results, "Check-out today" — stays labelled
// while scrolling. The canvas background hides rows scrolling underneath it.
const SectionTitle = styled.h2`
  position: sticky;
  top: var(--fd-bar-height, 97px);
  z-index: 20;
  margin: 0 0 0.6rem;
  padding: 0.5rem 0;
  font-size: 1rem;
  color: ${theme.ink};
  background: ${theme.canvas};
  border-bottom: 1px solid ${theme.border};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RowCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.75rem 0.9rem;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  flex-wrap: wrap;
  cursor: pointer;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  &:hover {
    border-color: ${theme.gold};
    box-shadow: 0 2px 10px -4px rgba(26, 26, 26, 0.18);
  }
`;

// The guest info fills the left; the open-booking link + status badges sit in a right rail so the
// card reads balanced instead of everything crowding the left edge.
const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1 1 240px;
  min-width: 0;
`;

const RowSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.45rem;
  flex: 0 0 auto;
`;

const RowHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const RoomBadge = styled.span`
  font-weight: 800;
  font-size: 25px;
  line-height: 1.1;
  color: ${theme.ink};
  min-width: 3rem;
`;

const GuestName = styled.span`
  flex: 0 1 auto;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${theme.ink};
`;

// Voucher code as a distinct pill right beside the guest name so the FO can spot it at a glance.
const VoucherPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.7rem;
  border-radius: 999px;
  background: ${theme.goldSoft};
  color: ${theme.goldDark};
  border: 1px solid ${theme.gold};
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  white-space: nowrap;
`;

// The room charge is still owed, beside the guest name. The row already carries a "Booking belum
// lunas" pill further along, but the pills sit at the end of a long row and get skimmed past; the
// marker puts the same fact where the eye already is. Same amber, deliberately — one meaning, one
// colour.
const UnpaidMark = styled.span`
  align-items: center;
  color: #b45309;
  cursor: help;
  display: inline-flex;
`;

// Needs-attention marker (OTA email + online deposit paid) — shared idea with the drawer's AttnMark.
const AttnMark = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.35rem;
  border-radius: 6px;
  background: #fde68a;
  color: #92400e;
  cursor: help;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.3rem;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${(props) => props.$fg};
  background: ${(props) => props.$bg};
`;

const NotesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
`;

const DateLine = styled.div`
  font-size: 0.85rem;
  color: ${theme.ink700};
  strong {
    color: ${theme.ink};
  }
`;

// A transit guest still in the room after their slot ended — the one transit state the desk has to
// chase, so it reads as an alert rather than as part of the date line.
const OverduePill = styled.span`
  background: #fdf3e3;
  border-radius: 999px;
  color: #b45309;
  font-size: 0.72rem;
  font-weight: 800;
  margin-left: 0.4rem;
  padding: 0.12rem 0.45rem;
`;

const MetaLine = styled.div`
  font-size: 0.8rem;
  color: ${theme.muted};
  display: flex;
  flex-wrap: wrap;
  gap: 0.15rem 0.5rem;
`;

const CommentBox = styled.div`
  padding: 0.45rem 0.6rem;
  border: 1px solid #f0dfc0;
  border-radius: 8px;
  background: #fff8ec;
  font-size: 0.82rem;
  color: ${theme.ink700};
  white-space: pre-wrap;
  word-break: break-word;
`;

const OpenLink = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${theme.goldDark};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const Empty = styled.div`
  padding: 1.5rem 1rem;
  text-align: center;
  color: ${theme.muted};
  background: #ffffff;
  border: 1px dashed ${theme.border};
  border-radius: 10px;
`;

// --- Advanced booking search -------------------------------------------------------------------

const SearchCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.85rem 1rem;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  margin-bottom: 1rem;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  flex: 1 1 260px;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.7rem;
  border: 1px solid ${theme.border};
  border-radius: 9px;
  background: #ffffff;
  color: ${theme.muted};
  &:focus-within {
    border-color: ${theme.gold};
  }
`;

const SearchInput = styled.input`
  flex: 1 1 auto;
  border: 0;
  outline: none;
  font-size: 0.9rem;
  color: ${theme.ink};
  background: transparent;
`;

const Chip = styled.button`
  padding: 0.3rem 0.75rem;
  border: 1px solid ${(props) => (props.$active ? theme.gold : theme.border)};
  border-radius: 999px;
  background: ${(props) => (props.$active ? theme.goldSoft : "#ffffff")};
  color: ${(props) => (props.$active ? theme.ink : theme.ink700)};
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
`;

const AdvToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  border: 0;
  background: transparent;
  color: ${theme.goldDark};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const AdvGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 0.5rem 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px dashed ${theme.border};
`;

const AdvField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${theme.muted};
  input,
  select {
    padding: 0.35rem 0.5rem;
    border: 1px solid ${theme.border};
    border-radius: 8px;
    font-size: 0.82rem;
    font-weight: 400;
    color: ${theme.ink};
    background: #ffffff;
  }
`;

const ResultStatus = styled.span`
  margin-left: auto;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${(props) => props.$fg};
  background: ${(props) => props.$bg};
`;

const MoreHint = styled.div`
  margin-top: 0.4rem;
  font-size: 0.78rem;
  color: ${theme.muted};
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const dotPulse = keyframes`
  0%, 80%, 100% {
    opacity: 0.25;
  }
  40% {
    opacity: 1;
  }
`;

// Search state line under the box: typing -> searching -> found N / not found (or the min-char hint).
const STATUS_COLOR = {
  min: theme.muted,
  typing: theme.muted,
  fetching: theme.goldDark,
  found: "#1c7a43",
  not_found: "#b91c1c",
};

const SearchStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.15rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${(props) => props.$color};
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const TypingDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  i {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: currentColor;
    animation: ${dotPulse} 1.2s infinite ease-in-out;
  }
  i:nth-child(2) {
    animation-delay: 0.15s;
  }
  i:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

const BADGE_GREEN = { bg: "#e7f5ec", fg: "#1c7a43" };
const BADGE_AMBER = { bg: "#fdf0e1", fg: "#b45309" };
const BADGE_BLUE = { bg: "#e7f0fb", fg: "#1d5fa8" };

// Row status pills. Check-in readiness only shows for a reserved arrival; deposit shows its paid
// channel (online = Xendit, cash = manual at the desk) or that it is still owed.
const lineBadges = (line) => {
  const badges = [];
  if (line.status === "reserved") {
    badges.push(
      line.can_check_in
        ? { key: "badge_checkin_ready", ...BADGE_GREEN }
        : { key: "badge_checkin_blocked", ...BADGE_AMBER },
    );
  }
  badges.push(
    line.rate_paid
      ? { key: "badge_booking_paid", ...BADGE_GREEN }
      : { key: "badge_booking_unpaid", ...BADGE_AMBER },
  );
  if (line.deposit_status === "paid") {
    badges.push(
      line.deposit_method === "online"
        ? { key: "badge_deposit_online", ...BADGE_BLUE }
        : { key: "badge_deposit_cash", ...BADGE_GREEN },
    );
  } else if (line.deposit_status === "pending_payment") {
    badges.push({ key: "badge_deposit_unpaid", ...BADGE_AMBER });
  }
  return badges;
};

const RESULT_STATUS_META = {
  reserved: { key: "line_reserved", bg: "#fdf0e1", fg: "#b45309" },
  checked_in: { key: "line_checked_in", bg: "#e7f0fb", fg: "#1d5fa8" },
  checked_out: { key: "line_checked_out", bg: "#eef1f5", fg: "#334155" },
  canceled: { key: "line_canceled", bg: "#fdecec", fg: "#c0392b" },
};

const EMPTY_ADVANCED = {
  voucher_code: "",
  ezee_unique_id: "",
  guest_name: "",
  room_code: "",
  line_status: "",
  check_in_from: "",
  check_in_to: "",
  check_out_from: "",
  check_out_to: "",
};

// Wait until the user has stopped typing for this long before searching, and only once the free-text
// box has at least this many characters — a short query matches too many rows to be useful and
// hammers the endpoint on every keystroke.
const SEARCH_DEBOUNCE_MS = 1000;
const SEARCH_MIN_CHARS = 3;

const metaParts = (line, t) => {
  const parts = [];
  if (line.source) {
    parts.push(`${t("source")}: ${line.source}`);
  }
  if (line.email) {
    parts.push(line.email);
  }
  if (line.phone) {
    parts.push(line.phone);
  }
  return parts;
};

// One booking row, memoized: the arrivals/departures lists no longer re-render row-by-row on
// unrelated page updates (search results arriving, the drawer opening, the 30s poll). Only rows whose
// own data or open-state changed re-render, because every prop passed in is referentially stable.
const BookingRow = memo((props) => {
  const line = props.line;
  const t = props.t;
  const lang = props.lang;
  const comment = cleanComment(line.comment);
  const meta = metaParts(line, t);
  const resultMeta =
    props.kind === "search" ? RESULT_STATUS_META[line.status] : null;
  return (
    <RowCard
      role="button"
      tabIndex={0}
      onClick={() => props.onOpenDrawer(line.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          props.onOpenDrawer(line.id);
        }
      }}
    >
      <RowMain>
        <RowHead>
          <RoomBadge>{line.room_code || "—"}</RoomBadge>
          {/* Beranda has no GuestCell wrapper — the badge is a direct sibling inside RowHead,
              which already lays its children out in a row. */}
          <BookingSourceIcon
            sourceKey={line.source_key}
            label={
              line.source_key === "walkin"
                ? t("source_walkin")
                : t("source_unknown")
            }
          />
          <GuestName>{line.guest_name || "—"}</GuestName>
          {line.rate_paid === false ? (
            <UnpaidMark title={t("unpaid_mark")} aria-label={t("unpaid_mark")}>
              <HandCoins size={12} aria-hidden="true" />
            </UnpaidMark>
          ) : null}
          {line.email_attention ? (
            <AttnMark title={t("email_attention_short")}>
              <AlertTriangle size={12} />
            </AttnMark>
          ) : null}
          {line.voucher ? <VoucherPill>{line.voucher}</VoucherPill> : null}
          {resultMeta ? (
            <ResultStatus $bg={resultMeta.bg} $fg={resultMeta.fg}>
              {t(resultMeta.key)}
            </ResultStatus>
          ) : null}
        </RowHead>

        <DateLine>
          {line.stay_kind === "transit" ? (
            <>
              <strong>
                {timeWithDayPart(line.transit_from, t)} →{" "}
                {timeWithDayPart(line.transit_to, t)}
              </strong>
              {line.transit_duration_hours
                ? ` · ${line.transit_duration_hours} ${t("transit_hours")}`
                : ""}
              {line.transit_crosses_midnight
                ? ` · ${t("transit_crosses_midnight")}`
                : ""}
              {line.transit_overdue ? (
                <OverduePill>{t("transit_overdue")}</OverduePill>
              ) : null}
            </>
          ) : (
            <>
              <strong>{formatDate(line.check_in_date, lang, false)}</strong> →{" "}
              <strong>{formatDate(line.check_out_date, lang, false)}</strong>
              {line.nights ? ` · ${line.nights} ${t("nights")}` : ""}
            </>
          )}
        </DateLine>

        {meta.length > 0 ? (
          <MetaLine>
            {meta.map((part, index) => (
              <span key={part}>
                {index > 0 ? "· " : ""}
                {part}
              </span>
            ))}
          </MetaLine>
        ) : null}

        <NotesRow>
          <FoNotesButton
            counts={props.count}
            onClick={() =>
              props.onOpenComments({
                id: line.id,
                room_code: line.room_code,
                guest_name: line.guest_name,
              })
            }
          />
          {comment ? (
            <GuestNotesButton
              hasNote
              active={props.isOpen}
              onClick={() => props.onToggleComment(line.id)}
            />
          ) : null}
        </NotesRow>
        {props.isOpen ? <CommentBox>{comment}</CommentBox> : null}
      </RowMain>

      <RowSide>
        <OpenLink type="button" onClick={() => props.onOpenDrawer(line.id)}>
          {t("open_booking")} →
        </OpenLink>
        <BadgeRow>
          {lineBadges(line).map((badge) => (
            <Badge key={badge.key} $bg={badge.bg} $fg={badge.fg}>
              {t(badge.key)}
            </Badge>
          ))}
        </BadgeRow>
      </RowSide>
    </RowCard>
  );
});
BookingRow.displayName = "BookingRow";

// The Beranda search, isolated so typing only re-renders this small subtree — never the heavy
// arrivals/departures lists. Owns the query + filters + debounce; a keystroke does nothing but reset
// the timer. Completed results are lifted up via props.onResult, at most once per debounced search.
const BookingSearch = memo((props) => {
  const { t } = useT();
  const onResult = props.onResult;
  const [query, setQuery] = useState("");
  const [statusChip, setStatusChip] = useState("");
  const [advOpen, setAdvOpen] = useState(false);
  const [advanced, setAdvanced] = useState(EMPTY_ADVANCED);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [debouncedAdvanced, setDebouncedAdvanced] = useState(EMPTY_ADVANCED);
  const [searching, setSearching] = useState(false);
  const [matchCount, setMatchCount] = useState(null);
  const [matchMore, setMatchMore] = useState(false);
  const seqRef = useRef(0);

  const runSearch = useCallback(
    async (paramsObj) => {
      const seq = ++seqRef.current;
      const entries = Object.entries(paramsObj).filter(
        ([, value]) => String(value || "").trim() !== "",
      );
      if (entries.length === 0) {
        onResult(null);
        setSearching(false);
        setMatchCount(null);
        setMatchMore(false);
        return;
      }
      setSearching(true);
      const qs = new URLSearchParams(Object.fromEntries(entries)).toString();
      try {
        const res = await apiFetch(`/frontdesk_v2/search?${qs}`);
        if (seq === seqRef.current) {
          const data = res?.data || null;
          onResult(data);
          setMatchCount(data ? (data.total ?? 0) : 0);
          setMatchMore(Boolean(data?.has_more));
        }
      } catch {
        if (seq === seqRef.current) {
          onResult(null);
          setMatchCount(null);
          setMatchMore(false);
        }
      }
      if (seq === seqRef.current) {
        setSearching(false);
      }
    },
    [onResult],
  );

  // Debounce the free-text box: a valid (>= min chars) query only becomes searchable after the user
  // pauses; clearing it (or dropping below the minimum) takes effect at once.
  useEffect(() => {
    const trimmed = query.trim();
    const next = trimmed.length >= SEARCH_MIN_CHARS ? trimmed : "";
    const delay = next === "" ? 0 : SEARCH_DEBOUNCE_MS;
    const timer = setTimeout(() => setDebouncedQuery(next), delay);
    return () => clearTimeout(timer);
  }, [query]);

  // Advanced filters carry typed free-text too, so debounce them the same way — otherwise each
  // keystroke there fires its own request.
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedAdvanced(advanced),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [advanced]);

  // Search off the debounced query + advanced filters; the status chips apply immediately.
  useEffect(() => {
    runSearch({ q: debouncedQuery, status: statusChip, ...debouncedAdvanced });
  }, [debouncedQuery, statusChip, debouncedAdvanced, runSearch]);

  // Search state for the indicator: typing -> searching -> found/not-found (min-char hint first).
  const trimmed = query.trim();
  const belowMin = trimmed.length > 0 && trimmed.length < SEARCH_MIN_CHARS;
  const pendingQuery =
    trimmed.length >= SEARCH_MIN_CHARS && trimmed !== debouncedQuery;
  let status = "idle";
  if (belowMin) {
    status = "min";
  } else if (searching) {
    status = "fetching";
  } else if (pendingQuery) {
    status = "typing";
  } else if (matchCount !== null) {
    status = matchCount > 0 ? "found" : "not_found";
  }

  return (
    <SearchCard>
      <SearchRow>
        <SearchBox>
          <Search size={16} aria-hidden="true" />
          <SearchInput
            type="text"
            placeholder={t("search_placeholder")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </SearchBox>
        {[
          { value: "", label: t("chip_all") },
          { value: "reserved", label: t("reserved") },
          { value: "paid", label: t("chip_paid") },
          { value: "canceled", label: t("chip_canceled") },
        ].map((chip) => (
          <Chip
            key={chip.value || "all"}
            type="button"
            $active={statusChip === chip.value}
            onClick={() => setStatusChip(chip.value)}
          >
            {chip.label}
          </Chip>
        ))}
        <AdvToggle type="button" onClick={() => setAdvOpen(!advOpen)}>
          <SlidersHorizontal size={14} />
          {t("adv_toggle")}
        </AdvToggle>
      </SearchRow>
      {status !== "idle" ? (
        <SearchStatus $color={STATUS_COLOR[status]}>
          {status === "typing" ? (
            <TypingDots>
              <i />
              <i />
              <i />
            </TypingDots>
          ) : status === "fetching" ? (
            <Spinner size={14} />
          ) : status === "found" ? (
            <Check size={14} />
          ) : status === "not_found" ? (
            <SearchX size={14} />
          ) : (
            <Info size={14} />
          )}
          <span>
            {status === "found"
              ? `${matchCount}${matchMore ? "+" : ""} ${
                  matchCount === 1 && !matchMore
                    ? t("search_matched_one")
                    : t("search_matched")
                }`
              : status === "typing"
                ? t("search_typing")
                : status === "fetching"
                  ? t("search_searching")
                  : status === "not_found"
                    ? t("search_no_match")
                    : t("search_min_chars")}
          </span>
        </SearchStatus>
      ) : null}
      {advOpen ? (
        <AdvGrid>
          <AdvField>
            {t("voucher")}
            <input
              type="text"
              value={advanced.voucher_code}
              onChange={(event) =>
                setAdvanced({ ...advanced, voucher_code: event.target.value })
              }
            />
          </AdvField>
          <AdvField>
            {t("guest")}
            <input
              type="text"
              value={advanced.guest_name}
              onChange={(event) =>
                setAdvanced({ ...advanced, guest_name: event.target.value })
              }
            />
          </AdvField>
          <AdvField>
            {t("room_label")}
            <input
              type="text"
              value={advanced.room_code}
              onChange={(event) =>
                setAdvanced({ ...advanced, room_code: event.target.value })
              }
            />
          </AdvField>
          <AdvField>
            {t("ezee_label")}
            <input
              type="text"
              value={advanced.ezee_unique_id}
              onChange={(event) =>
                setAdvanced({
                  ...advanced,
                  ezee_unique_id: event.target.value,
                })
              }
            />
          </AdvField>
          <AdvField>
            {t("ls_label")}
            <select
              value={advanced.line_status}
              onChange={(event) =>
                setAdvanced({ ...advanced, line_status: event.target.value })
              }
            >
              <option value="">{t("chip_all")}</option>
              <option value="reserved">{t("line_reserved")}</option>
              <option value="checked_in">{t("line_checked_in")}</option>
              <option value="checked_out">{t("line_checked_out")}</option>
              <option value="canceled">{t("line_canceled")}</option>
            </select>
          </AdvField>
          <AdvField>
            {t("ci_from")}
            <input
              type="date"
              value={advanced.check_in_from}
              onChange={(event) =>
                setAdvanced({
                  ...advanced,
                  check_in_from: event.target.value,
                })
              }
            />
          </AdvField>
          <AdvField>
            {t("ci_to")}
            <input
              type="date"
              value={advanced.check_in_to}
              onChange={(event) =>
                setAdvanced({ ...advanced, check_in_to: event.target.value })
              }
            />
          </AdvField>
          <AdvField>
            {t("co_from")}
            <input
              type="date"
              value={advanced.check_out_from}
              onChange={(event) =>
                setAdvanced({
                  ...advanced,
                  check_out_from: event.target.value,
                })
              }
            />
          </AdvField>
          <AdvField>
            {t("co_to")}
            <input
              type="date"
              value={advanced.check_out_to}
              onChange={(event) =>
                setAdvanced({ ...advanced, check_out_to: event.target.value })
              }
            />
          </AdvField>
        </AdvGrid>
      ) : null}
    </SearchCard>
  );
});
BookingSearch.displayName = "BookingSearch";

const TodayIndex = (props) => {
  const { t, lang } = useT();
  const counters = props.counters || {};
  const arrivals = props.arrivals || [];
  const departures = props.departures || [];
  const transits = props.transits || [];
  const [openComment, setOpenComment] = useState(null);
  const [commentsLine, setCommentsLine] = useState(null);
  const [drawerLineId, setDrawerLineId] = useState(readDrawerLineId);
  const [searchResult, setSearchResult] = useState(null);

  // Keep ?drawer=<lineId> in the URL so a reload (F5) reopens the same booking drawer.
  useDrawerUrlSync(drawerLineId);

  // Whole-page freshness: background partial reload of ALL props (counters, lists, pulse band, note
  // badges). preserveEqualProps keeps unchanged prop references, so nothing re-renders needlessly.
  usePoll(30_000);

  // Stable handlers so the memoized BookingRow only re-renders when its own data/open-state changes.
  const openDrawer = useCallback((id) => setDrawerLineId(id), []);
  const openComments = useCallback((lineData) => setCommentsLine(lineData), []);
  const toggleComment = useCallback(
    (id) => setOpenComment((current) => (current === id ? null : id)),
    [],
  );

  const counterDefs = [
    {
      key: "arrivals",
      label: t("c_arrivals"),
      value: counters.arrivals,
      Icon: LogIn,
    },
    {
      key: "departures",
      label: t("c_departures"),
      value: counters.departures,
      Icon: LogOut,
    },
    {
      key: "in_house",
      label: t("c_in_house"),
      value: counters.in_house,
      sub: `${counters.in_house_guests ?? 0} ${t("c_guests")}`,
      Icon: BedDouble,
    },
    {
      key: "to_clean",
      label: t("c_to_clean"),
      value: counters.to_clean,
      Icon: Brush,
    },
    // Transit is a separate product, not a subset of arrivals — the arrivals/departures scopes are
    // explicitly overnight-only, so without its own counter a day-use guest is invisible here.
    {
      key: "transits",
      label: t("c_transits"),
      value: counters.transits,
      sub: counters.transits_overdue
        ? `${counters.transits_overdue} ${t("transit_overdue")}`
        : null,
      Icon: Timer,
    },
  ];

  const mergedCounts = useMemo(
    () => ({
      ...(searchResult?.notes_counts || {}),
      ...(props.notes_counts || {}),
    }),
    [searchResult, props.notes_counts],
  );

  const renderRow = (line, kind) => (
    <BookingRow
      key={line.id}
      line={line}
      kind={kind}
      t={t}
      lang={lang}
      count={mergedCounts[line.id]}
      isOpen={openComment === line.id}
      onOpenDrawer={openDrawer}
      onOpenComments={openComments}
      onToggleComment={toggleComment}
    />
  );

  const renderList = (lines, kind) => {
    if (lines.length === 0) {
      return <Empty>{t("none_here")}</Empty>;
    }
    return <List>{lines.map((line) => renderRow(line, kind))}</List>;
  };

  return (
    <PageShell refreshMs={30_000}>
      <Head title={t("today_title")} />
      <Header>
        <div>
          <Title>{t("today_title")}</Title>
          <Subtitle>{t("today_subtitle")}</Subtitle>
        </div>
      </Header>

      {props.property_status ? (
        <PropertyStatusBar data={props.property_status} />
      ) : null}

      <BookingSearch onResult={setSearchResult} />

      {searchResult ? (
        <Section style={{ marginTop: 0, marginBottom: "1rem" }}>
          <SectionTitle>
            {t("search_results")} ({searchResult.total}
            {searchResult.has_more ? "+" : ""})
          </SectionTitle>
          {searchResult.lines.length === 0 ? (
            <Empty>{t("search_none")}</Empty>
          ) : (
            <>
              <List>
                {searchResult.lines.map((line) => renderRow(line, "search"))}
              </List>
              {searchResult.has_more ? (
                <MoreHint>{t("search_more")}</MoreHint>
              ) : null}
            </>
          )}
        </Section>
      ) : null}

      <Counters>
        {counterDefs.map((def) => {
          const Icon = def.Icon;
          return (
            <CounterCard key={def.key}>
              <IconWrap>
                <Icon size={22} />
              </IconWrap>
              <div>
                <CounterValue>{def.value ?? 0}</CounterValue>
                <CounterLabel>{def.label}</CounterLabel>
                {def.sub ? <CounterSub>{def.sub}</CounterSub> : null}
              </div>
            </CounterCard>
          );
        })}
      </Counters>

      <Section>
        <SectionTitle>{t("arrivals_today")}</SectionTitle>
        {renderList(arrivals, "arrival")}
      </Section>

      <Section>
        <SectionTitle>{t("departures_today")}</SectionTitle>
        {renderList(departures, "departure")}
      </Section>

      {transits.length > 0 ? (
        <Section>
          <SectionTitle>{t("transits_today")}</SectionTitle>
          {renderList(transits, "transit")}
        </Section>
      ) : null}

      {drawerLineId ? (
        <BookingDrawer
          key={drawerLineId}
          lineId={drawerLineId}
          date={props.date}
          onClose={() => setDrawerLineId(null)}
          onOpenComments={(line) => setCommentsLine(line)}
        />
      ) : null}

      {commentsLine ? (
        <FoCommentsModal
          line={commentsLine}
          onClose={(dirty) => {
            setCommentsLine(null);
            if (dirty) {
              router.reload({ preserveScroll: true });
            }
          }}
        />
      ) : null}
    </PageShell>
  );
};

export default TodayIndex;
